import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'

import { OcrImageViewer } from '@/modules/base/components/ocr/index'
import FileService from '@/modules/file/fileService'
import PlaceHolder from '@/modules/file/components/placeholder'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import lang from '@/lang'
import ThemeInterface from '@/theme-interface'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    width: '100%',
    justifyContent: ({ hasNoPreview }: { hasNoPreview: boolean, scale: number }) => hasNoPreview ? 'center' : 'unset',
  },
  img: {
    transform: ({ scale }) => `scale(${scale || 1})`,
    transformOrigin: 'top left',
  },
  modal: {
    width: 500,
    padding: 40,
  },
  select: {
    margin: '10px 0px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 20,
  },
  image: {
    display: 'flex',
    justifyContent: 'center',
    objectFit: 'contain',
  },
  previewBanner: {
    position: 'absolute',
    background: CssHelper.alpha(theme.grey1, 0.5),
    transform: 'rotateZ(-45deg) translateY(-19px) translateX(-63px)',
    padding: '10px 70px',
    fontSize: 20,
    color: 'white',
  },
}))

interface ReceiptPreviewerItemImageInterface {
  ocrModeEnabled: boolean
  customClasses?: string[]
  width: number
  rotate: number
  scale: number
  receipt: any
  hasNoPreview: boolean
  showPreviewBanner?: boolean,
  onOcrResult: (ocrResult: any) => void
}

export const ReceiptPreviewerItemImage = (props: ReceiptPreviewerItemImageInterface) => {
  const {
    ocrModeEnabled,
    customClasses,
    width,
    rotate,
    scale: initialScale,
    receipt,
    hasNoPreview,
    onOcrResult,
    showPreviewBanner,
  } = props
  const styles = useStyles(props)
  const classes = { ...styles, ...customClasses }
  const [url, setUrl] = useState(receipt.url)
  const [turn, setTurn] = useState(rotate / 90)
  const [scale, setScale] = useState(initialScale)
  const imgRef = useRef<HTMLImageElement>()

  if (turn !== rotate / 90) {
    setTurn(rotate / 90)
  }

  if (scale !== initialScale) {
    setScale(initialScale)
  }

  useEffect(() => {
    centerImage()
  }, [scale, url, width])

  useEffect(() => {
    setUrl(props?.receipt?.url)
  }, [receipt?.url])

  useEffect(() => {
    rotateAndScale(props, newURl => {
      setUrl(newURl)
    })
  }, [turn, url])

  const centerImage = () => {
    if (!imgRef.current) {
      return
    }

    const clientRect = imgRef.current.getBoundingClientRect()

    if (!clientRect?.width || !width) {
      imgRef.current.style.marginLeft = ''
      return
    }

    imgRef.current.style.marginLeft = clientRect.width < width
      ? ((width - clientRect.width) / 2) + 'px'
      : ''
  }

  const content = hasNoPreview
    ? <PlaceHolder mimetype={receipt?.mimetype} thumbnailUrl={receipt?.thumbnailUrl} objectUrl={null} />
    : ocrModeEnabled
      ? (<OcrImageViewer
        src={url}
        customClasses={{ image: classes.image }}
        onOcrResult={onOcrResult} />)
      : <img src={url} ref={imgRef} className={classes.img} onLoad={centerImage} />

  return (
    <>
      <div className={classes.root}>
        { content }
        {showPreviewBanner ? <div className={classes.previewBanner}>{lang.generics.preview}</div> : null}
      </div>
    </>
  )
}

ReceiptPreviewerItemImage.props = {
  receipt: PropTypes.object.isRequired,
  showPreviewBanner: PropTypes.bool,
}

const rotateAndScaleImage = (props, src, callback) => {
  const img = new Image()
  img.src = src
  img.crossOrigin = 'Anonymous'
  img.onload = function() {
    const canvas = document.createElement('canvas')
    canvas.style.position = 'absolute'
    const ctx = canvas.getContext('2d')
    ctx.save()
    // const width = (props.rotate === 0 || props.rotate === 180) ? img.width * props.scale : img.height * props.scale
    const width = (props.rotate === 0 || props.rotate === 180) ? img.width : img.height
    // const height = (props.rotate === 0 || props.rotate === 180) ? img.height * props.scale : img.width * props.scale
    const height = (props.rotate === 0 || props.rotate === 180) ? img.height : img.width
    canvas.width = width
    canvas.height = height

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.translate(width / 2, height / 2)
    ctx.rotate(props.rotate * Math.PI / 180)
    // ctx.scale(props.scale, props.scale)
    ctx.drawImage(img, -img.width / 2, -img.height / 2)
    ctx.restore()
    callback(canvas.toDataURL())
  }
}

const rotateAndScale = (props, callback) => {
  if (props.receipt.url.includes('file')) {
    FileService.fetchFileDataByUrl(props.receipt.url)
      .then(data => {
        rotateAndScaleImage(props, data, callback)
      })
  } else {
    rotateAndScaleImage(props, props.receipt.url, callback)
  }
}

export default ReceiptPreviewerItemImage
